import React, { useCallback, useEffect, useState } from "react";
import api, { brl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Trophy, TrendingDown, Wallet, FileSpreadsheet, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BAR_RADIUS = [6, 6, 0, 0];

export default function Reports() {
  const [top, setTop] = useState([]);
  const [low, setLow] = useState([]);
  const [fin, setFin] = useState(null);
  const [dreData, setDreData] = useState(null);
  const [dreMonth, setDreMonth] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  useEffect(() => {
    api.get("/reports/top-products").then((r) => setTop(r.data));
    api.get("/reports/low-products").then((r) => setLow(r.data));
    api.get("/reports/financial").then((r) => setFin(r.data));
  }, []);

  const loadDre = useCallback((m) => api.get(`/reports/dre?month=${m}`).then((r) => setDreData(r.data)), []);
  useEffect(() => { loadDre(dreMonth); }, [loadDre, dreMonth]);

  const clearSales = async () => {
    if (!confirm("ATENÇÃO: isto apaga TODO o histórico de vendas e zera 'mais vendidos'. Continuar?")) return;
    const typed = prompt("Digite ZERAR para confirmar:");
    if (typed !== "ZERAR") return;
    try {
      const { data } = await api.post("/sales/clear", null, { params: { confirm: "ZERAR" } });
      toast.success(`${data.deleted} vendas removidas`);
      api.get("/reports/top-products").then((r) => setTop(r.data));
      api.get("/reports/financial").then((r) => setFin(r.data));
      loadDre(dreMonth);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const topChart = top.slice(0, 8).map((p) => ({ name: p.name.slice(0, 18), qty: p.sold_qty }));

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Análise</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Relatórios</h1>
        </div>
        <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50"
          onClick={clearSales} data-testid="clear-sales-btn">
          <Trash2 className="w-4 h-4 mr-2" /> Zerar histórico de vendas
        </Button>
      </div>

      <Tabs defaultValue="top">
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="top" data-testid="tab-top"><Trophy className="w-4 h-4 mr-2" /> Mais Vendidos</TabsTrigger>
          <TabsTrigger value="low" data-testid="tab-low"><TrendingDown className="w-4 h-4 mr-2" /> Menos Vendidos</TabsTrigger>
          <TabsTrigger value="fin" data-testid="tab-fin"><Wallet className="w-4 h-4 mr-2" /> Financeiro Geral</TabsTrigger>
          <TabsTrigger value="dre" data-testid="tab-dre"><FileSpreadsheet className="w-4 h-4 mr-2" /> DRE</TabsTrigger>
        </TabsList>

        <TabsContent value="top" className="space-y-4 mt-4">
          <Card className="card-outline">
            <CardContent className="p-5 h-72">
              {topChart.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Bar dataKey="qty" fill="#2563EB" radius={BAR_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="text-slate-400 h-full flex items-center justify-center">Sem dados</div>}
            </CardContent>
          </Card>
          <Card className="card-outline">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>#</TableHead><TableHead>Produto</TableHead><TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Vendidos</TableHead>
                  <TableHead className="text-right">Preço</TableHead>
                  <TableHead className="text-right">Receita</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {top.map((p, i) => (
                    <TableRow key={p.id} data-testid={`top-row-${i}`}>
                      <TableCell><Badge variant="secondary">#{i + 1}</Badge></TableCell>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell>{p.category || "—"}</TableCell>
                      <TableCell className="text-right kpi-num">{p.sold_qty}</TableCell>
                      <TableCell className="text-right kpi-num">{brl(p.price)}</TableCell>
                      <TableCell className="text-right kpi-num text-emerald-600">{brl(p.price * p.sold_qty)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="low" className="mt-4">
          <Card className="card-outline">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Produto</TableHead><TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Vendidos</TableHead>
                  <TableHead className="text-right">Estoque parado</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {low.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell>{p.category || "—"}</TableCell>
                      <TableCell className="text-right kpi-num text-rose-600">{p.sold_qty}</TableCell>
                      <TableCell className="text-right kpi-num">{p.stock}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fin" className="mt-4">
          {fin && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { label: "Receita bruta", value: fin.revenue, color: "text-emerald-600" },
                { label: "CMV (custo)", value: fin.cogs, color: "text-rose-600" },
                { label: "Lucro bruto", value: fin.gross_profit, color: "text-blue-600" },
                { label: "Margem bruta", value: fin.gross_margin + "%", color: "text-slate-900", raw: true },
                { label: "Despesas pagas", value: fin.operating_expenses, color: "text-rose-600" },
                { label: "Lucro líquido", value: fin.net_profit, color: "text-emerald-700" },
                { label: "Total a pagar", value: fin.total_payable, color: "text-amber-600" },
                { label: "Total a receber", value: fin.total_receivable, color: "text-blue-600" },
                { label: "Nº de vendas", value: fin.sales_count, color: "text-slate-900", raw: true },
              ].map((k) => (
                <Card key={k.label} className="card-outline"><CardContent className="p-5">
                  <div className="text-xs uppercase tracking-widest text-slate-500">{k.label}</div>
                  <div className={"kpi-num text-2xl mt-2 " + k.color}>{k.raw ? k.value : brl(k.value)}</div>
                </CardContent></Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="dre" className="mt-4">
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm font-medium">Mês:</label>
            <input type="month" value={dreMonth} onChange={(e) => setDreMonth(e.target.value)}
              className="border border-slate-200 rounded-md px-3 py-1.5 text-sm" data-testid="dre-month-input" />
          </div>
          {dreData && (
            <Card className="card-outline">
              <CardContent className="p-6">
                <h3 className="font-display text-lg font-bold mb-4">DRE — {dreData.month}</h3>
                <table className="w-full text-sm" data-testid="dre-table">
                  <tbody>
                    <tr className="border-b"><td className="py-2 font-semibold">(+) Receita Bruta</td><td className="text-right kpi-num">{brl(dreData.revenue)}</td></tr>
                    <tr className="border-b"><td className="py-2 pl-6 text-slate-600">(-) Descontos concedidos</td><td className="text-right kpi-num text-rose-600">-{brl(dreData.discounts)}</td></tr>
                    <tr className="border-b bg-slate-50"><td className="py-2 font-semibold">(=) Receita Líquida</td><td className="text-right kpi-num text-blue-600">{brl(dreData.net_revenue)}</td></tr>
                    <tr className="border-b"><td className="py-2 pl-6 text-slate-600">(-) CMV (custo das mercadorias vendidas)</td><td className="text-right kpi-num text-rose-600">-{brl(dreData.cogs)}</td></tr>
                    <tr className="border-b bg-slate-50"><td className="py-2 font-semibold">(=) Lucro Bruto</td><td className="text-right kpi-num text-emerald-600">{brl(dreData.gross_profit)}</td></tr>
                    <tr className="border-b"><td className="py-2 pl-6 text-slate-500 italic">Margem Bruta</td><td className="text-right kpi-num text-slate-500">{dreData.gross_margin}%</td></tr>
                    <tr className="border-b"><td className="py-2 pl-6 text-slate-600">(-) Despesas Operacionais (pagas)</td><td className="text-right kpi-num text-rose-600">-{brl(dreData.operating_expenses)}</td></tr>
                    {dreData.expenses_by_category.map((c) => (
                      <tr key={c.category} className="text-xs text-slate-500">
                        <td className="py-1 pl-12">• {c.category}</td>
                        <td className="text-right kpi-num">{brl(c.amount)}</td>
                      </tr>
                    ))}
                    <tr className="border-b border-t-2 border-slate-900 bg-blue-50"><td className="py-3 font-bold text-base">(=) Lucro Operacional</td><td className="text-right kpi-num text-lg font-bold text-blue-700">{brl(dreData.operating_profit)}</td></tr>
                    <tr><td className="py-2 pl-6 text-slate-500 italic">Margem Operacional</td><td className="text-right kpi-num text-slate-500">{dreData.operating_margin}%</td></tr>
                    <tr><td className="py-2 pl-6 text-slate-500 italic">Nº de vendas no período</td><td className="text-right kpi-num text-slate-500">{dreData.sales_count}</td></tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
